import { useState } from "react";
import { Building2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { ErrorCard } from "@/components/shared/ErrorCard";
import { EmptyState } from "@/components/shared/EmptyState";
import {
  useDashboardTopCentres,
  type TopCentresMetric,
  type TopCentresRange,
} from "@/hooks/useDashboard";
import { formatCurrency } from "@/utils/format";

const METRICS: Array<{ value: TopCentresMetric; label: string }> = [
  { value: "revenue", label: "Revenue" },
  { value: "bookings", label: "Bookings" },
];

const RANGE: TopCentresRange = "30d";
const LIMIT = 5;

export const TopCentresList = () => {
  const [metric, setMetric] = useState<TopCentresMetric>("revenue");
  const { data, isLoading, error, refetch } = useDashboardTopCentres({
    metric,
    range: RANGE,
    limit: LIMIT,
  });

  const items = data?.items ?? [];
  const max = items.reduce((acc, it) => Math.max(acc, it.value), 0);

  return (
    <div className="bg-card rounded-card border border-border p-5">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-semibold text-foreground">Top centres</h3>
          <p className="text-sm text-muted-foreground mt-0.5">Last 30 days</p>
        </div>
        <div className="inline-flex rounded-btn border border-border bg-muted/50 p-0.5">
          {METRICS.map((m) => (
            <button
              key={m.value}
              type="button"
              onClick={() => setMetric(m.value)}
              className={`px-3 py-1 text-xs font-medium rounded-btn transition-colors ${
                metric === m.value
                  ? "bg-card text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 rounded-md" />
          ))}
        </div>
      ) : error ? (
        <ErrorCard message={(error as Error).message} onRetry={refetch} />
      ) : items.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="No data yet"
          description={
            metric === "revenue"
              ? "No paid payments in the last 30 days."
              : "No bookings in the last 30 days."
          }
        />
      ) : (
        <ol className="space-y-3">
          {items.map((item, index) => {
            const pct = max > 0 ? (item.value / max) * 100 : 0;
            return (
              <li key={item.centreId} className="flex items-center gap-3">
                <span className="text-xs font-semibold text-muted-foreground w-5">
                  {index + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-sm font-medium text-foreground truncate">
                      {item.name}
                    </span>
                    <span className="text-sm font-semibold text-foreground shrink-0">
                      {metric === "revenue"
                        ? formatCurrency(item.value)
                        : item.value}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-muted-foreground truncate">
                      {item.city}
                    </span>
                  </div>
                  <div className="h-1 bg-muted rounded-full overflow-hidden mt-1.5">
                    <div
                      className="h-full bg-primary rounded-full"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

export default TopCentresList;
